import type { ReactNode } from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import {
  useRightPanelTripDrawerPresentation,
  type TripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import ModalProvider from 'contexts/ModalProvider'
import ModalContext from 'contexts/ModalContext'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import flightSearchWidgetStateReducer, {
  createEmptyFlightSearchWidgetFlightItem,
  FlightSearchWidgetActions,
  initialState,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type {
  FlightSearchWidgetState,
  FlightSearchWidgetDispatchAction,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetInitialFocus } from 'contexts/Flights/FlightSearchWidget/types'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import TripContextFlightSearchWidget from 'tripPlanner/components/FlightSearch/TripContextFlightSearchWidget'
import FlightSearchWidgetMobile from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetMobile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import {
  FlightSegment,
  FlightSortOptions,
  FlightsFareTypes,
  fareClassToUrlMap,
  parseFareClassFromUrl,
  FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES,
} from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  resetFlightsTimer,
  hasFlightSessionExpired,
} from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { FlightSessionCountdownClock } from 'components/Common/FlightSessionCountdownClock/FlightSessionCountdownClock'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import type { TransportOption, TripStop } from 'tripPlanner/api/types'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { cacheShortlistedJourneys } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/shortlistedJourneyCache'
import { selectFlightFareFamilies } from 'tripPlanner/components/Chat/Flights/selectFlightFareFamilies'
import ChatFlightSortingBar from 'tripPlanner/components/Chat/Flights/ChatFlightSortingBar'
import { FlightResultsControls } from 'tripPlanner/components/Chat/Flights/flightResultsLayout'
import { buildFlightContext, getSelectedFarePricing } from 'tripPlanner/components/Chat/Flights/buildFlightContext'
import useChatFlightResultsSorting from 'tripPlanner/components/Chat/Flights/useChatFlightResultsSorting'
import DrawerFlightCard from 'tripPlanner/components/Chat/Flights/DrawerFlightCard'
import { FlightRouteStepperView, type FlightRouteStep } from 'components/Flights/FlightsSearch/FlightRouteStepper/FlightRouteStepper'
import {
  getSegmentKey,
  getSegmentKeys,
  getSearchItinerary,
} from 'tripPlanner/components/Chat/Flights/flightSegmentUtils'
import { buildMultiCityFlightMessage } from 'tripPlanner/components/Chat/Flights/multiCityFlightMessage'
import { useTripFlightFilters } from 'tripPlanner/components/Chat/Flights/useTripFlightFilters'
import TripFlightFiltersPanel from 'tripPlanner/components/Chat/Flights/TripFlightFiltersPanel'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { resolveSearchCalendarFocusDate } from 'tripPlanner/utils/searchCalendarFocus'
import TripDrawerAssistantBridge from '../TripDrawerAssistantBridge'
import { buildFlightSearchDrawerContext } from '../../utils/drawerAskAIContext'
import FlightSearchResultCard from './FlightSearchResultCard'
import FlightJourneyDetailsDrawer from './FlightJourneyDetailsDrawer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getInitialFlightSearchFocus } from './initialFlightSearchFocus'
import { getValidFlightSearchDate } from './flightSearchDateUtils'
import usePersistTripChatRecentSearch from 'tripPlanner/hooks/usePersistTripChatRecentSearch'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { showTripActionPendingSnackbar } from 'tripPlanner/components/Snackbar/tripActionSnackbar'
import { buildFlightSearchSubmittedActivityEntry } from './buildFlightSearchSubmittedActivityEntry'
import { buildLastFlightSearchCommittedStateEntry } from './buildLastFlightSearchCommittedStateEntry'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { buildFlightRecentSearchUpsertBody } from 'tripPlanner/utils/tripChatRecentSearchPayload'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineEditIcon from 'LuxKit/Icons/line/LineEditIcon'
import { buildStandaloneFlightSearchUrl, type StandaloneFlightSearchParam } from 'lib/flights/flightUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import zIndex from 'styles/tools/z-index'
import { drawerBodyInlinePaddingStyles } from '../drawerBodyLayout.styles'

const SearchWrapper = styled.div`
  container: trip-flight-drawer-search / inline-size;
  padding-block: ${rem(20)};
  ${drawerBodyInlinePaddingStyles}
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(12)};
  }

  /*
    The shared airport field (SearchFormTextField) covers its own input with an opaque
    display overlay inset by 1px. That fill lands on the input's 1px border and, at
    fractional device pixel ratios, washes it out to near-white — the From/To fields lose
    their outer edges while the neighbouring date fields, which have no overlay, keep
    theirs. Let the overlay draw text only, and hide the input beneath it so nothing shows
    through. Scoped here rather than in the shared component, so only this drawer changes.
  */
  .display-field {
    background-color: transparent;
  }

  span:has(+ .display-field):not(:focus-within) > input {
    color: transparent;

    &::placeholder {
      color: transparent;
    }
  }
`

const ContentWrapper = styled.div<{ $compactTop?: boolean, $compactBottom?: boolean }>`
  padding-block-start: ${props => rem(props.$compactTop ? 8 : 12)};
  padding-block-end: ${props => rem(props.$compactBottom ? 0 : 12)};
  ${drawerBodyInlinePaddingStyles}
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  ${mediaQueryUp.tablet} {
    padding-block-start: ${props => rem(props.$compactTop ? 12 : 24)};
    padding-block-end: ${props => rem(props.$compactBottom ? 0 : 24)};
  }
`

const CountdownWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeaderBanner};
  display: flex;
  justify-content: center;
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  box-sizing: border-box;

  ${mediaQueryUp.tablet} {
    /* TripDrawer's body adds 15px on each side; let the banner reach its edges. */
    width: calc(100% + ${rem(30)});
    margin-inline: ${rem(-15)};
  }
`

interface SelectedFlightTileProps {
  step: FlightRouteStep
  date: string
  changeLabel: string
  isChangeDisabled: boolean
  onChange: (index: number) => void
  onViewDetails: (journey: App.JourneyV2) => void
}

function SelectedFlightTile({ step, date, changeLabel, isChangeDisabled, onChange, onViewDetails }: SelectedFlightTileProps) {
  const handleChange = useCallback(() => onChange(step.index), [onChange, step.index])
  const handleViewDetails = useCallback(() => onViewDetails(step.flight), [onViewDetails, step.flight])

  return (
    <VerticalSpacer gap={8}>
      <BodyText variant="medium">{date}</BodyText>
      <DrawerFlightCard
        journey={step.flight}
        onSelect={handleChange}
        onViewDetails={handleViewDetails}
        isSelectDisabled={isChangeDisabled}
        selectLabel={changeLabel}
        selectIcon={<LineEditIcon />}
        showPrice={false}
      />
    </VerticalSpacer>
  )
}

const EMPTY_ITINERARY = [
  { departureAirport: '', arrivalAirport: '', departureDate: '' },
]

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: EMPTY_ITINERARY,
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

interface FlightSearchDefaults extends Omit<Pick<Props, 'originAirportCode' | 'destinationAirportCode' | 'departureDate' | 'returnDate' | 'fareType'>, 'itinerary'> {
  itinerary?: Array<Partial<FlightSearchParams['itinerary'][number]>>
}

export function hasCompleteFlightSearchDefaults({
  originAirportCode,
  destinationAirportCode,
  departureDate,
  returnDate,
  itinerary,
  fareType,
}: FlightSearchDefaults): boolean {
  if (itinerary?.length && fareType === FlightsFareTypes.MULTI_CITY) {
    return itinerary.every(leg => Boolean(leg.departureAirport && leg.arrivalAirport && leg.departureDate))
  }

  if (fareType === FlightsFareTypes.RETURN) {
    return Boolean(originAirportCode && destinationAirportCode && departureDate && returnDate)
  }

  return Boolean(originAirportCode && destinationAirportCode && departureDate)
}

interface Props {
  isOpen: boolean
  onClose: () => void
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  /**
   * The itinerary's loose `YYYY-MM` travel-month hint, used to pick which month
   * the date pickers open on when no departure date is known.
   */
  indicativeSearchMonth?: string
  itinerary?: FlightSearchParams['itinerary']
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  passengers?: number
  autoSearch?: boolean
  onSendMessage?: (message: string) => void
  isStreaming?: boolean
  onCartFlightSelect?: (
    selection: FlightSearchDrawerCartSelection,
  ) => Promise<boolean | void> | boolean | void
  isCartSubmitting?: boolean
  existingFlights?: Array<TransportOption>
  /** Rendered under the "Saved" tab of the drawer (e.g. shortlisted flights). */
  headerSlot?: ReactNode
  /** Whether there are saved/shortlisted flights to surface under the "Saved" tab. */
  hasSavedFlights?: boolean
  showMultiCity?: boolean
  tripStopId?: number | null
  /**
   * The trip's stops, drawn behind the desktop date calendar as read-only
   * context. Omitted, the standard flight calendar is used unchanged.
   */
  tripStops?: ReadonlyArray<TripStop>
}

export interface FlightSearchDrawerCartSelection {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  multiCityJourneys?: Array<App.JourneyV2>
  searchParams: FlightSearchParams
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
}

interface ContentProps {
  onSendMessage?: (message: string) => void
  isStreaming: boolean
  onCartFlightSelect?: (
    selection: FlightSearchDrawerCartSelection,
  ) => Promise<boolean | void> | boolean | void
  isCartSubmitting: boolean
  onClose: () => void
  existingFlightFingerprints: Set<string>
  autoSearch?: boolean
  headerSlot?: ReactNode
  hasSavedFlights?: boolean
  showMultiCity?: boolean
  drawerPresentation: TripDrawerPresentation
  tripStops?: ReadonlyArray<TripStop>
  desktopInitialFocus?: FlightSearchWidgetInitialFocus
  mobileInitialFocus?: FlightSearchWidgetInitialFocus
  tripStopId?: number | null
}

function getFlightFingerprint(
  flightNumber: string | undefined,
  departureAirport: string | undefined,
  arrivalAirport: string | undefined,
): string {
  return `${flightNumber ?? ''}|${departureAirport ?? ''}|${
    arrivalAirport ?? ''
  }`
}

function isJourneyAlreadyAdded(
  journey: App.JourneyV2,
  fingerprints: Set<string>,
): boolean {
  const flights = journey.flightGroup.flights
  const firstFlight = flights[0]
  const lastFlight = flights[flights.length - 1]
  return fingerprints.has(
    getFlightFingerprint(
      firstFlight.flightNumber,
      firstFlight.departureAirport,
      lastFlight.arrivalAirport,
    ),
  )
}

function buildFlightSearchIntentParams({
  flights,
  occupants,
  fareType,
  fareCabin,
}: Pick<FlightSearchWidgetState, 'flights' | 'occupants' | 'fareType' | 'fareCabin'>): FlightSearchParams {
  return {
    itinerary: getSearchItinerary(flights, fareType?.value) ?? EMPTY_ITINERARY,
    occupancies: [occupants],
    fareClass: fareCabin?.value
      ? fareClassToUrlMap[fareCabin.value] ?? 'economy'
      : 'economy',
    fareType: fareType?.value,
  }
}

function buildFlightSearchContextLabel(params: FlightSearchParams | undefined): string {
  const itinerary = params?.itinerary ?? EMPTY_ITINERARY
  const firstLeg = itinerary[0]
  const lastLeg = itinerary[itinerary.length - 1]
  const from = firstLeg?.departureAirport
  const to = lastLeg?.arrivalAirport

  if (from && to) {
    return `${from} to ${to} flights`
  }

  return 'Flights'
}

function buildStandaloneSearchParams(state: FlightSearchWidgetState): StandaloneFlightSearchParam | undefined {
  const firstFlight = state.flights[0]
  const fareType = state.fareType?.value
  if (!firstFlight?.departureAirport?.airportCode || !firstFlight.arrivalAirport?.airportCode || !firstFlight.checkinDate || !fareType || fareType === FlightsFareTypes.MULTI_CITY) {
    return undefined
  }
  if (fareType === FlightsFareTypes.RETURN && !firstFlight.checkoutDate) {
    return undefined
  }

  const occupancy = state.occupants

  return {
    originAirportCode: firstFlight.departureAirport.airportCode,
    originAirportName: firstFlight.departureAirport.airportName,
    destinationAirportCode: firstFlight.arrivalAirport.airportCode,
    destinationAirportName: firstFlight.arrivalAirport.airportName,
    departDate: firstFlight.checkinDate.format(ISO_DATE_FORMAT),
    returnDate: fareType === FlightsFareTypes.RETURN && firstFlight.checkoutDate ? firstFlight.checkoutDate.format(ISO_DATE_FORMAT) : null,
    adults: occupancy.adults,
    children: occupancy.children ?? 0,
    infants: occupancy.infants ?? 0,
    childrenAge: occupancy.childrenAge?.join(','),
    fareClass: state.fareCabin?.value ? fareClassToUrlMap[state.fareCabin.value] ?? 'economy' : 'economy',
    fareType: fareType === FlightsFareTypes.RETURN ? 'return' : 'oneWay',
  }
}

function FlightSearchDrawerContent({
  onSendMessage,
  onCartFlightSelect,
  isCartSubmitting,
  onClose,
  existingFlightFingerprints,
  autoSearch,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
  isStreaming,
  drawerPresentation,
  desktopInitialFocus,
  mobileInitialFocus,
  tripStopId,
  tripStops,
}: ContentProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { chatId } = useChatStreamStatus()
  const persistRecentSearch = usePersistTripChatRecentSearch(chatId)
  const eventBus = useTripPlannerEventBusOptional()
  const isDesktopSearchWidgetActive = useScreenSizeUp('tablet')
  const isCartMode = !!onCartFlightSelect
  const canSelectFlight = isCartMode || !!onSendMessage
  const shouldUseMobileDetailsDrawer = !isDesktopSearchWidgetActive
  const isResultSelectionDisabled = isCartSubmitting || (shouldUseMobileDetailsDrawer && isStreaming)
  const [searchParams, setSearchParams] = useState<
    FlightSearchParams | undefined
  >(undefined)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchVersion, setSearchVersion] = useState(0)

  const [activeSegmentKey, setActiveSegmentKey] = useState<string>(
    FlightSegment.DEPARTING,
  )
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(
    FlightSortOptions.Best,
  )
  const [selectedFlights, setSelectedFlights] = useState<
    Record<string, App.JourneyV2>
  >({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<
    Record<string, App.FlightFareFamily>
  >({})
  const [detailsJourney, setDetailsJourney] = useState<App.JourneyV2 | undefined>(undefined)
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false)

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && currentRegionCode === 'AU'
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const isMultiCity = fareType?.value === FlightsFareTypes.MULTI_CITY
  const defaultSegmentCount = isReturn ? 2 : Math.max(1, flights.length)
  const segments = useMemo<Array<string>>(
    () =>
      getSegmentKeys(
        searchParams?.itinerary.length ?? defaultSegmentCount,
        isReturn,
      ),
    [defaultSegmentCount, isReturn, searchParams?.itinerary.length],
  )
  const activeSegment = segments.includes(activeSegmentKey)
    ? activeSegmentKey
    : segments[0] ?? activeSegmentKey
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate
      ? moment(flight.checkinDate).format(ISO_DATE_FORMAT)
      : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate
      ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT)
      : undefined
  }, [flights])

  const handleSearch = useCallback((isExplicitSubmit: boolean = false) => {
    const itinerary = getSearchItinerary(flights, fareType?.value)
    if (!itinerary) {
      return
    }

    const nextVersion = searchVersion + 1
    const params: FlightSearchParams = {
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value
        ? fareClassToUrlMap[fareCabin.value] ?? 'economy'
        : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    }

    const standaloneParams = buildStandaloneSearchParams({
      flights,
      occupants,
      fareType,
      fareCabin,
    })
    const firstLeg = itinerary[0]
    const lastLeg = itinerary[itinerary.length - 1]
    const originAirportCode = standaloneParams?.originAirportCode ?? firstLeg?.departureAirport
    const destinationAirportCode = standaloneParams?.destinationAirportCode ?? lastLeg?.arrivalAirport

    const departureDate = standaloneParams?.departDate ?? firstLeg?.departureDate
    const returnDate = standaloneParams?.returnDate ?? undefined

    if (originAirportCode && destinationAirportCode) {
      persistRecentSearch(buildFlightRecentSearchUpsertBody({
        tripStopId,
        originAirportCode,
        originAirportName: standaloneParams?.originAirportName ?? firstLeg?.departureAirport,
        destinationAirportCode,
        destinationAirportName: standaloneParams?.destinationAirportName ?? lastLeg?.arrivalAirport,
        departureDate,
        returnDate,
        fareType: standaloneParams?.fareType ?? fareType?.value,
        fareClass: standaloneParams?.fareClass ?? (
          fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy'
        ),
        occupants,
      }))
      if (isExplicitSubmit && departureDate) {
        const searchParams = { originAirportCode, destinationAirportCode, departureDate, returnDate, fareClass: params.fareClass, fareType: params.fareType, occupants, itinerary }
        eventBus?.dispatch({
          type: 'trip_planner_surface_activity',
          entries: [buildFlightSearchSubmittedActivityEntry(searchParams)],
        })
        eventBus?.dispatch({
          type: 'trip_planner_committed_state',
          entries: [buildLastFlightSearchCommittedStateEntry(tripStopId, searchParams)],
        })
      }
    }

    setSearchVersion(nextVersion)
    setSearchParams(params)
    setHasSearched(true)
    setActiveSegmentKey(
      getSegmentKey(0, fareType?.value === FlightsFareTypes.RETURN),
    )
    setSelectedFlights({})
    setSelectedFareFamilies({})
    setIsDetailsDrawerOpen(false)
    setDetailsJourney(undefined)
  }, [eventBus, fareCabin, fareType, flights, occupants, persistRecentSearch, searchVersion, tripStopId])

  // Explicit customer submits only -- see handleSearch's isExplicitSubmit param. Passed to both
  // the desktop widget's onSearchAirport and the mobile form's onSubmit below.
  const handleExplicitSearch = useCallback(() => {
    handleSearch(true)
  }, [handleSearch])

  const hasAutoSearched = useRef(false)
  useEffect(() => {
    if (autoSearch && !hasAutoSearched.current) {
      hasAutoSearched.current = true
      handleSearch()
    }
  }, [autoSearch, handleSearch])

  const {
    journeys,
    fetching,
    error,
    searchResultsTimestamp,
    key: searchKey,
  } = useSearchFlights(searchParams ?? EMPTY_SEARCH_PARAMS, {
    disabled: !searchParams,
    segments,
    activeSegmentKey: activeSegment,
    selectedFlights,
    selectedFareFamilies,
    isStandalone: canRequestLuxPlusFlightPricing,
  })

  const cachedSearchId = useAppSelector(
    (state) => state.flights.searchV2Flights[searchKey]?.searchId,
  )
  const cachedSessionId = useAppSelector(
    (state) => state.flights.searchV2Flights[searchKey]?.sessionId,
  )

  const displayJourneys = useChatFlightResultsSorting({
    journeys,
    activeSegmentKey: activeSegment,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  // Filter the sorted, same-day-aware results. Filter options are built from
  // `displayJourneys` so they stay stable as the user narrows the selection;
  // filters reset whenever the search or active segment changes.
  const tripFlightFilters = useTripFlightFilters(displayJourneys, {
    resetKey: `${searchKey}-${activeSegment}`,
  })
  const { visibleJourneys, resetAllFilters } = tripFlightFilters

  const expiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp)
      .add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes')
      .toDate()
      .getTime()
  }, [searchResultsTimestamp])

  useEffect(() => {
    if (searchResultsTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp, searchResultsTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  useEffect(() => {
    if (!hasSearched || fetching || !expiryTimestamp) return

    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }

    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }

    const timeout = setTimeout(() => {
      handleSearchRef.current()
    }, remaining)

    return () => clearTimeout(timeout)
  }, [hasSearched, fetching, expiryTimestamp])

  const currentSegmentIndex = useMemo(() => {
    return Math.max(
      0,
      segments.findIndex((s) => s === activeSegment),
    )
  }, [segments, activeSegment])
  const activeItineraryLeg = searchParams?.itinerary[currentSegmentIndex]
  const completedRouteSteps = useMemo<Array<FlightRouteStep>>(() =>
    segments.slice(0, currentSegmentIndex).flatMap((segment, index) => {
      const journey = selectedFlights[segment]
      const leg = searchParams?.itinerary[index]
      return journey && leg ? [{
        segment,
        index,
        flight: journey,
        originCode: leg.departureAirport,
        destinationCode: leg.arrivalAirport,
      }] : []
    }),
  [currentSegmentIndex, searchParams, segments, selectedFlights])

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      setActiveSegmentKey(segments[stepIndex])
      setIsDetailsDrawerOpen(false)
      setDetailsJourney(undefined)
      // Keep the clicked step's own selection; only clear the legs after it (their
      // choices may depend on this leg, and the user is re-opening this step to
      // review/change it, not to lose it).
      const keysToRemove = segments.slice(stepIndex + 1)
      setSelectedFlights((prev) => {
        const next = { ...prev }
        for (const key of keysToRemove) delete next[key]
        return next
      })
      setSelectedFareFamilies((prev) => {
        const next = { ...prev }
        for (const key of keysToRemove) delete next[key]
        return next
      })
    },
    [segments],
  )

  const handleViewDetails = useCallback((journey: App.JourneyV2) => {
    setDetailsJourney(journey)
    setIsDetailsDrawerOpen(true)
  }, [])

  const handleDetailsClose = useCallback(() => {
    setIsDetailsDrawerOpen(false)
  }, [])

  const handleDetailsAfterClose = useCallback(() => {
    setDetailsJourney(undefined)
  }, [])

  const getSelectedFlightDate = useCallback(
    (journey: App.JourneyV2) => {
      const flight = journey.flightGroup.flights[0]
      return dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
    },
    [dateFormatter],
  )

  const handleSelectFlight = useCallback(
    async(data: { journey: App.JourneyV2 }) => {
      const currentSegmentKey = activeSegment
      const currentSegmentIndex = segments.findIndex(
        (key) => key === currentSegmentKey,
      )

      if (currentSegmentIndex < segments.length - 1) {
        // Choose this leg's fare while the flight is still in view. The next
        // search step uses the chosen fare to find compatible flights.
        let fareFamily: App.FlightFareFamily | undefined
        if (!isMultiCity && (onSendMessage || onCartFlightSelect) && !isStreaming) {
          const fareResult = await selectFlightFareFamilies({
            departingJourney: data.journey,
            occupants: searchParams?.occupancies[0] ?? { adults: 1 },
            searchId: cachedSearchId,
            sessionId: cachedSessionId,
            showModal,
          })
          if (fareResult.status !== 'selected') return
          fareFamily = fareResult.selection.departingFareFamily
        }
        setSelectedFareFamilies((prev) => {
          const next = { ...prev }
          delete next[currentSegmentKey]
          if (fareFamily) next[currentSegmentKey] = fareFamily
          return next
        })
        setSelectedFlights((prev) => ({
          ...prev,
          [currentSegmentKey]: data.journey,
        }))
        setActiveSegmentKey(segments[currentSegmentIndex + 1])
        setIsDetailsDrawerOpen(false)
        return
      }

      const departingJourney = selectedFlights.departing
      const returningJourney = isReturn ? data.journey : undefined
      const selectedJourneyList = [
        ...segments.slice(0, -1).map((segment) => selectedFlights[segment]),
        data.journey,
      ].filter((journey): journey is App.JourneyV2 => !!journey)

      const outbound =
        selectedJourneyList[0] ?? departingJourney ?? data.journey
      const outboundFlights = outbound.flightGroup.flights
      const from = outboundFlights[0].departureAirport
      const finalJourney =
        selectedJourneyList[selectedJourneyList.length - 1] ?? outbound
      const finalFlights = finalJourney.flightGroup.flights
      const to = finalFlights[finalFlights.length - 1].arrivalAirport

      const cacheSelectedJourneys = () => {
        let journeysToCache: Array<App.JourneyV2> = [data.journey]
        if (isMultiCity) {
          journeysToCache = selectedJourneyList
        } else if (isReturn && departingJourney && returningJourney) {
          journeysToCache = [departingJourney, returningJourney]
        }
        // The drawer search requests LuxPlus pricing for charge-eligible members
        // (isStandalone gate), so cache journeys under that same mode. Charge-path
        // reads (getCachedJourney with the gate) then reuse the member searchId; a
        // non-member's public journeys are cached (and reused) as public.
        cacheShortlistedJourneys(
          journeysToCache,
          cachedSearchId,
          cachedSessionId,
          canRequestLuxPlusFlightPricing,
        )
      }

      if (isCartMode) {
        if (
          !onCartFlightSelect ||
          !searchParams ||
          isCartSubmitting ||
          isStreaming
        ) {
          return
        }
        const didSelect = await Promise.resolve(
          onCartFlightSelect({
            departingJourney: outbound,
            returningJourney,
            multiCityJourneys: isMultiCity ? selectedJourneyList : undefined,
            searchParams,
            searchId: cachedSearchId,
            sessionId: cachedSessionId,
            searchExpiry: expiryTimestamp,
          }),
        ).catch(() => false)
        if (didSelect !== false) {
          cacheSelectedJourneys()
          setDetailsJourney(undefined)
          onClose()
        }
        return
      }

      if (!onSendMessage || isStreaming) return

      // Complete any remaining fare choice before sending the flight and fare
      // together to the assistant. The earlier leg's choice is reused.
      const fareResult = await selectFlightFareFamilies({
        departingJourney: outbound,
        returningJourney,
        occupants: searchParams?.occupancies[0] ?? { adults: 1 },
        searchId: cachedSearchId,
        sessionId: cachedSessionId,
        showModal,
        isMultiCity,
      })
      if (fareResult.status !== 'selected') return
      const { departingFareFamily, returningFareFamily } = fareResult.selection
      const farePricing = getSelectedFarePricing({
        departingJourney: outbound,
        returningJourney: isReturn ? returningJourney : undefined,
        departingFareFamily,
        returningFareFamily,
      })

      cacheSelectedJourneys()

      let userMessage: string
      let contextContent: string

      if (isMultiCity) {
        const groupCurrency = selectedJourneyList[0]?.currencyCode
        const combinedTotal = undefined
        const message = buildMultiCityFlightMessage({
          intl,
          from,
          to,
          selectedJourneyList,
          groupCurrency,
          combinedTotal,
          buildFlightContext,
        })
        userMessage = message.userMessage
        contextContent = message.contextContent
      } else if (isReturn && departingJourney && returningJourney) {
        userMessage = intl.formatMessage(
          {
            id: 'ZE1Bw2jDpdHXdHmw312L',
            defaultMessage: 'Add return flight from {from} to {to}',
            description:
              'User chat message shown when adding a return flight from the flight search drawer',
          },
          {
            from,
            to,
          },
        )
        contextContent = `Departing flight:\n${buildFlightContext(
          departingJourney,
          departingFareFamily,
          farePricing,
        )}\n\nReturning flight:\n${buildFlightContext(returningJourney, returningFareFamily, farePricing)}`
      } else {
        userMessage = intl.formatMessage(
          {
            id: 'mtc-4uBZ7D3tkBZV8ueR',
            defaultMessage: 'Add flight from {from} to {to}',
            description:
              'User chat message shown when adding a one-way flight from the flight search drawer',
          },
          {
            from,
            to,
          },
        )
        contextContent = buildFlightContext(data.journey, departingFareFamily, farePricing)
      }

      const searchMeta = [
        `Search expiry: ${expiryTimestamp}`,
        `Search params: ${searchParams ? JSON.stringify(searchParams) : ''}`,
      ].join('\n')

      const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta}</Context>`
      // Flights reach the itinerary through a chat message rather than the add-to-trip
      // helpers, so they need their own pending toast or the add lands with no feedback.
      showTripActionPendingSnackbar(intl, 'add', intl.formatMessage(
        {
          id: 'Q41LDH8mIidTPMyaVXiQ',
          defaultMessage: '{from} to {to}',
          description: 'Names a flight by its route under the Adding to trip snackbar heading in the trip planner',
        },
        { from, to },
      ))
      onSendMessage(message)
      setDetailsJourney(undefined)
      onClose()
    },
    [
      intl,
      segments,
      activeSegment,
      selectedFlights,
      isReturn,
      isMultiCity,
      isCartMode,
      isCartSubmitting,
      onCartFlightSelect,
      onSendMessage,
      isStreaming,
      onClose,
      expiryTimestamp,
      searchParams,
      cachedSearchId,
      cachedSessionId,
      canRequestLuxPlusFlightPricing,
      showModal,
    ],
  )

  const savedHeading = intl.formatMessage({
    id: 'UkCP7iQhTsHWmenEq93v',
    defaultMessage: 'Saved',
    description:
      'Section heading for the saved flights in the trip planner flight drawer',
  })

  const exploreHeading = intl.formatMessage({
    id: 'hn-UDYbNIkT2Q6CwUXBK',
    defaultMessage: 'Explore more',
    description:
      'Section heading above the flight search in the trip planner flight drawer',
  })

  const noResultsMessage = intl.formatMessage({
    id: 'P51jHD0yS8A8bb49PJWq',
    defaultMessage:
      'No flights found for your search. Try different dates or airports.',
    description:
      'Message shown when no flight results are found in the trip planner drawer',
  })

  const selectFlightLabel = currentSegmentIndex === segments.length - 1
    ? intl.formatMessage({
        id: '_r0LZ1-gKOSRCr2u9yIC',
        defaultMessage: 'Book now',
        description: 'Button label to confirm the final flight selection in the trip planner drawer',
      })
    : intl.formatMessage({
        id: 'FpuEHM82GUVuDqZ0SICN',
        defaultMessage: 'Select',
        description: 'Button label to choose a flight before selecting the next trip segment',
      })
  // A matching leg in the trip does not mean this return or multi-city
  // selection has already been added as an itinerary.
  const checkAlreadyAdded = segments.length === 1 && !isCartMode

  const departingFlightLabel = intl.formatMessage({
    id: 'qJfCIiKntFNv9yNLHAHJ',
    defaultMessage: 'Departing flight',
    description: 'Label for departing flight segment in trip planner drawer',
  })

  const returningFlightLabel = intl.formatMessage({
    id: '4p1QhxNAMtAdkUW9nReN',
    defaultMessage: 'Returning flight',
    description: 'Label for returning flight segment in trip planner drawer',
  })

  const resultsHeaderLabel = useMemo(() => {
    if (isMultiCity) {
      return intl.formatMessage(
        {
          id: 'xmce4KMu4_6CYz9hwEpo',
          defaultMessage: 'Flight {flightNumber}',
          description:
            'Label for a multi-city flight segment with its sequence number',
        },
        { flightNumber: currentSegmentIndex + 1 },
      )
    }
    if (isReturn && isReturningStep) return returningFlightLabel
    return departingFlightLabel
  }, [
    intl,
    isMultiCity,
    isReturn,
    isReturningStep,
    currentSegmentIndex,
    departingFlightLabel,
    returningFlightLabel,
  ])

  const showResults = hasSearched && !fetching && displayJourneys.length > 0
  const showNoResults =
    hasSearched && !fetching && !error && displayJourneys.length === 0
  const showError = hasSearched && !fetching && !!error
  const showSavedFlightsSection = hasSavedFlights && !!headerSlot
  const flightContextIntentParams = useMemo(
    () => buildFlightSearchIntentParams({
      flights,
      occupants,
      fareType,
      fareCabin,
    }),
    [fareCabin, fareType, flights, occupants],
  )
  const flightContextLabel = useMemo(
    () => buildFlightSearchContextLabel(searchParams ?? flightContextIntentParams),
    [flightContextIntentParams, searchParams],
  )
  const flightContextDetails = useMemo(
    () => buildFlightSearchDrawerContext({
      intentParams: flightContextIntentParams,
      searchParams,
      hasSearched,
      fetching,
      hasError: !!error,
      activeSegmentLabel: resultsHeaderLabel,
      sort: flightSort,
      activeFilterCount: tripFlightFilters.activeFilterCount,
      displayJourneys,
      visibleJourneys,
      selectedFlights,
      searchId: cachedSearchId,
      sessionId: cachedSessionId,
      searchExpiry: expiryTimestamp || undefined,
    }),
    [
      flightContextIntentParams,
      searchParams,
      hasSearched,
      fetching,
      error,
      resultsHeaderLabel,
      flightSort,
      tripFlightFilters.activeFilterCount,
      displayJourneys,
      visibleJourneys,
      selectedFlights,
      cachedSearchId,
      cachedSessionId,
      expiryTimestamp,
    ],
  )
  const selectedFlightsHeading = intl.formatMessage({
    id: 'HkFV5xukE-9hGANWSrbH',
    defaultMessage: '{count, plural, one {Selected flight} other {Selected flights}}',
    description: 'Heading above chosen flight tiles while the traveller selects the next leg in trip planner',
  }, { count: completedRouteSteps.length })
  const changeSelectedFlightLabel = intl.formatMessage({
    id: 'nC5CkX2UNetujY07PisN',
    defaultMessage: 'Change',
    description: 'Button on the selected flight tile that returns to its search results to choose another flight',
  })
  return (
    <>
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen
        contextLabel={flightContextLabel}
        contextDetails={flightContextDetails}
      />

      {hasSearched && searchResultsTimestamp && (
        <CountdownWrapper>
          <FlightSessionCountdownClock />
        </CountdownWrapper>
      )}

      <SearchWrapper>
        <VerticalSpacer gap={24}>
          {showSavedFlightsSection && (
            <VerticalSpacer gap={12}>
              <Heading variant="heading4">{savedHeading}</Heading>
              {headerSlot}
            </VerticalSpacer>
          )}

          <VerticalSpacer gap={16}>
            {showSavedFlightsSection && (
              <Heading variant="heading4">{exploreHeading}</Heading>
            )}

            <CSSBreakpoint min="tablet">
              {tripStops?.length ? (
                <TripContextFlightSearchWidget
                  onSearchAirport={handleExplicitSearch}
                  showMultiCity={showMultiCity}
                  wrapControls
                  groupAirportDropdowns
                  initialFocus={isDesktopSearchWidgetActive ? desktopInitialFocus : undefined}
                  tripStops={tripStops}
                  contextStopId={tripStopId ?? undefined}
                />
              ) : (
                <FlightSearchWidgetDesktop
                  onSearchAirport={handleExplicitSearch}
                  showMultiCity={showMultiCity}
                  wrapControls
                  groupAirportDropdowns
                  initialFocus={isDesktopSearchWidgetActive ? desktopInitialFocus : undefined}
                />
              )}
            </CSSBreakpoint>
            <CSSBreakpoint max="mobile">
              <VerticalSpacer
                gap={12}
                as="form"
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault()
                  handleExplicitSearch()
                }}
              >
                <FlightSearchWidgetMobile
                  showMultiCity={showMultiCity}
                  initialFocus={isDesktopSearchWidgetActive ? undefined : mobileInitialFocus}
                />
                <TextButton
                  kind="primary"
                  size="large"
                  fit="full-width"
                  type="submit"
                >
                  {intl.formatMessage({
                    id: 'WGRNTZqigCaFkyNGLUr0',
                    defaultMessage: 'Search',
                    description:
                      'Search button label to initiate flight search in the trip planner drawer',
                  })}
                </TextButton>
              </VerticalSpacer>
            </CSSBreakpoint>
          </VerticalSpacer>
        </VerticalSpacer>
      </SearchWrapper>

      {hasSearched && (isReturn || isMultiCity) && searchParams && completedRouteSteps.length > 0 && (
        <ContentWrapper $compactBottom>
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={12}>
              <Heading variant="heading5">{selectedFlightsHeading}</Heading>
              {completedRouteSteps.map(step => (
                <SelectedFlightTile
                  key={step.segment}
                  step={step}
                  date={getSelectedFlightDate(step.flight)}
                  changeLabel={changeSelectedFlightLabel}
                  isChangeDisabled={isResultSelectionDisabled}
                  onChange={handleStepClick}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </VerticalSpacer>
            <FlightRouteStepperView
              selectedSegments={completedRouteSteps}
              activeSegmentIndex={currentSegmentIndex}
              fareType={fareType?.value}
              onChange={handleStepClick}
            />
          </VerticalSpacer>
        </ContentWrapper>
      )}

      {fetching && (
        <>
          <FlightsLoadingTopBar />
          <ContentWrapper>
            <LoadingAnimationBox variant="flights" />
          </ContentWrapper>
        </>
      )}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({
              id: 'W6e3tDO59GW7SKXKCf3P',
              defaultMessage:
                'Something went wrong while searching for flights. Please try again.',
              description:
                'Error message when flight search fails in the trip planner drawer',
            })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="light-foreground-alt">
            {noResultsMessage}
          </BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper $compactTop={completedRouteSteps.length > 0}>
          <FlightResultsControls>
            <ChatFlightSortingBar
              journeys={visibleJourneys}
              sort={flightSort}
              originAirportCode={activeItineraryLeg?.departureAirport}
              destinationAirportCode={activeItineraryLeg?.arrivalAirport}
              onChange={setFlightSort}
            />
            <TripFlightFiltersPanel
              filterControls={tripFlightFilters}
              originAirportCode={activeItineraryLeg?.departureAirport ?? ''}
              destinationAirportCode={activeItineraryLeg?.arrivalAirport ?? ''}
              sort={flightSort}
              onSortChange={setFlightSort}
            />

            {visibleJourneys.length === 0 && (
              <VerticalSpacer gap={12}>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {intl.formatMessage({
                    id: 'tHI6ugJkg6Xo9BikjkNq',
                    defaultMessage: 'No flights match your filters.',
                    description:
                      'Message shown when active filters exclude all flight results in the trip planner drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={resetAllFilters}
                >
                  {intl.formatMessage({
                    id: 'drpXi_9Ybwre8YQMfj49',
                    defaultMessage: 'Reset filters',
                    description:
                      'Button label to clear flight filters when no results match in the trip planner drawer',
                  })}
                </TextButton>
              </VerticalSpacer>
            )}

            {visibleJourneys.map((journey) => (
              <FlightSearchResultCard
                key={journey.id}
                journey={journey}
                onSelect={canSelectFlight ? handleSelectFlight : undefined}
                isAlreadyAdded={checkAlreadyAdded && isJourneyAlreadyAdded(
                  journey,
                  existingFlightFingerprints,
                )}
                isCartMode={isCartMode}
                isSelectDisabled={isResultSelectionDisabled}
                selectLabel={currentSegmentIndex < segments.length - 1 || shouldUseMobileDetailsDrawer ? selectFlightLabel : undefined}
                showSelectButton={canSelectFlight}
                onViewDetails={shouldUseMobileDetailsDrawer ? handleViewDetails : undefined}
              />
            ))}
          </FlightResultsControls>
        </ContentWrapper>
      )}
      {detailsJourney && (
        <FlightJourneyDetailsDrawer
          isOpen={isDetailsDrawerOpen}
          journey={detailsJourney}
          onClose={handleDetailsClose}
          onAfterClose={handleDetailsAfterClose}
          onSelect={canSelectFlight ? handleSelectFlight : undefined}
          selectLabel={selectFlightLabel}
          showSelectButton={canSelectFlight}
          isSelectDisabled={isCartSubmitting || isStreaming}
          isAlreadyAdded={checkAlreadyAdded && isJourneyAlreadyAdded(
            detailsJourney,
            existingFlightFingerprints,
          )}
        />
      )}
    </>
  )
}

function FlightSearchDrawer({
  isOpen,
  onClose,
  originAirportCode,
  originAirportName,
  destinationAirportCode,
  destinationAirportName,
  departureDate,
  returnDate,
  indicativeSearchMonth,
  itinerary,
  fareType: fareTypeProp,
  fareClass: fareClassProp,
  occupants,
  passengers,
  autoSearch,
  onSendMessage,
  isStreaming = false,
  onCartFlightSelect,
  isCartSubmitting = false,
  existingFlights,
  headerSlot,
  hasSavedFlights,
  showMultiCity,
  tripStopId,
  tripStops,
}: Props) {
  const intl = useIntl()
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const drawerPresentation = useRightPanelTripDrawerPresentation()

  const drawerTitle = intl.formatMessage({
    id: '0py3lHxthzacj0uaxcjK',
    defaultMessage: 'Flights',
    description: 'Heading for the flight search drawer in trip planner',
  })
  const browseAllResultsLabel = intl.formatMessage({
    id: 'w3Ky3OJt1VCY3yhGrapK',
    defaultMessage: 'Browse all results',
    description: 'Button label to open the full flight search results page from the flight drawer',
  })

  const existingFlightFingerprints = useMemo(() => {
    const set = new Set<string>()
    if (!existingFlights) return set
    for (const option of existingFlights) {
      const data = option.savedItemData
      if (data?.source === 'le_flight' && data.flightNumber) {
        set.add(
          getFlightFingerprint(
            data.flightNumber,
            data.departureAirport,
            data.arrivalAirport,
          ),
        )
      }
    }
    return set
  }, [existingFlights])

  const validDepartureDate = getValidFlightSearchDate(departureDate)
  const validReturnDate = getValidFlightSearchDate(returnDate)
  const validItinerary = useMemo(
    () => itinerary?.map((leg) => ({
      ...leg,
      departureDate: getValidFlightSearchDate(leg.departureDate),
    })),
    [itinerary],
  )
  /**
   * The trip's own date for this search, used when nothing more specific is
   * known. Without it a trip planned months out opens its flight calendar on
   * today, with the itinerary nowhere in sight — the resolver's last resort is
   * the current month, not the trip's.
   */
  const contextStopStartDate = useMemo(() => {
    if (!tripStops?.length) return undefined

    const datedStops = sortTripStopsByItineraryOrder([...tripStops])
      .filter(stop => stop.stopType !== 'ORIGIN' && stop.startDate)
    const contextStop = tripStopId == null
      ? undefined
      : datedStops.find(stop => stop.id === tripStopId)

    return contextStop?.startDate ?? datedStops[0]?.startDate
  }, [tripStops, tripStopId])

  const resolvedCalendarFocusMonth = useMemo(
    () => resolveSearchCalendarFocusDate({
      relevantConcreteStart: validDepartureDate ??
        validItinerary?.[0]?.departureDate ??
        contextStopStartDate,
      indicativeSearchMonth,
    }),
    [contextStopStartDate, indicativeSearchMonth, validDepartureDate, validItinerary],
  )

  const widgetInitialState = useMemo((): FlightSearchWidgetState => {
    const requestedFareType = fareTypeProp
      ? fareTypes.find((ft) => ft.value === fareTypeProp)
      : undefined
    // A multi-city fare type needs the legs to go with it. Opening from a single
    // saved leg gives us one, and a one-leg multi-city search is rejected by the
    // search API, so show that leg as a one-way instead.
    const hasMultiCityItinerary = (validItinerary?.length ?? 0) > 1
    const resolvedFareType =
      requestedFareType?.value === FlightsFareTypes.MULTI_CITY && !hasMultiCityItinerary
        ? fareTypes.find((ft) => ft.value === FlightsFareTypes.ONE_WAY) ?? requestedFareType
        : requestedFareType
    const normalizedFareClass = fareClassProp
      ? parseFareClassFromUrl(fareClassProp) ?? fareClassProp
      : undefined
    const resolvedFareCabin = normalizedFareClass
      ? fareCabins.find((fc) => fc.value === normalizedFareClass)
      : undefined

    const state: FlightSearchWidgetState = {
      ...initialState,
      fareType:
        resolvedFareType ??
        fareTypes.find((ft) => ft.value === FlightsFareTypes.ONE_WAY) ??
        fareTypes[0],
      fareCabin: resolvedFareCabin ?? fareCabins[0],
      calendarFocusMonth: resolvedCalendarFocusMonth,
    }

    if (occupants) {
      state.occupants = {
        ...state.occupants,
        ...occupants,
      }
    } else if (passengers && passengers > 0) {
      state.occupants = {
        ...state.occupants,
        adults: passengers,
      }
    }

    if (
      validItinerary?.length &&
      resolvedFareType?.value === FlightsFareTypes.MULTI_CITY
    ) {
      state.flights = validItinerary?.map((leg) =>
        createEmptyFlightSearchWidgetFlightItem({
          id: uuidV4(),
          departureAirport: {
            airportCode: leg.departureAirport,
            airportName: leg.departureAirport,
          } as App.AirportLocation,
          arrivalAirport: {
            airportCode: leg.arrivalAirport,
            airportName: leg.arrivalAirport,
          } as App.AirportLocation,
          checkinDate: leg.departureDate ? moment(leg.departureDate) : undefined,
        }),
      ) ?? state.flights
      return state
    }

    if (originAirportCode) {
      state.flights = [
        {
          ...state.flights[0],
          id: uuidV4(),
          departureAirport: {
            airportCode: originAirportCode,
            airportName: originAirportName ?? originAirportCode,
          } as App.AirportLocation,
        },
      ]
    }

    if (destinationAirportCode && state.flights[0]) {
      state.flights = [
        {
          ...state.flights[0],
          arrivalAirport: {
            airportCode: destinationAirportCode,
            airportName: destinationAirportName ?? destinationAirportCode,
          } as App.AirportLocation,
        },
      ]
    }

    if (validDepartureDate) {
      state.flights = [
        {
          ...state.flights[0],
          checkinDate: moment(validDepartureDate),
        },
      ]
    }

    if (validReturnDate) {
      state.flights = [
        {
          ...state.flights[0],
          checkoutDate: moment(validReturnDate),
        },
      ]
    }

    return state
  }, [
    fareTypes,
    fareCabins,
    fareTypeProp,
    fareClassProp,
    occupants,
    passengers,
    validItinerary,
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    validDepartureDate,
    validReturnDate,
    resolvedCalendarFocusMonth,
  ])

  const [widgetState, widgetDispatch] = useReducer<
    FlightSearchWidgetState,
    [FlightSearchWidgetDispatchAction]
  >(flightSearchWidgetStateReducer, widgetInitialState)
  // Sticky-mounted drawer: skip the mount-equal dispatch (initial state already
  // has this value) but keep syncing when indicative month arrives later.
  const hasSyncedCalendarFocusRef = useRef(false)
  useEffect(() => {
    if (!hasSyncedCalendarFocusRef.current) {
      hasSyncedCalendarFocusRef.current = true
      return
    }

    widgetDispatch({
      type: FlightSearchWidgetActions.SET_CALENDAR_FOCUS_MONTH,
      calendarFocusMonth: resolvedCalendarFocusMonth,
    })
  }, [resolvedCalendarFocusMonth])
  const desktopInitialFocus = useMemo(
    () => getInitialFlightSearchFocus(widgetInitialState, { includeDateFields: true }),
    [widgetInitialState],
  )
  const mobileInitialFocus = useMemo(
    () => getInitialFlightSearchFocus(widgetInitialState),
    [widgetInitialState],
  )
  const browseAllResultsUrl = useMemo(() => {
    if (onCartFlightSelect) {
      return undefined
    }

    const standaloneParams = buildStandaloneSearchParams(widgetState)
    return standaloneParams ? buildStandaloneFlightSearchUrl(standaloneParams) : undefined
  }, [onCartFlightSelect, widgetState])
  const headerActions = browseAllResultsUrl ? (
    <AssistChip
      variant="filled"
      size="medium"
      to={browseAllResultsUrl}
      endIcon={<LineExternalLinkAltIcon />}
    >
      {browseAllResultsLabel}
    </AssistChip>
  ) : undefined

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      direction="right-to-left"
      presentation={drawerPresentation}
      header={<TripDrawerHeaderTitle title={drawerTitle} />}
      headerActions={headerActions}
    >
      <FlightSearchWidgetStateProvider
        state={widgetState}
        dispatch={widgetDispatch}
      >
        <ModalProvider>
          <FlightSearchDrawerContent
            onSendMessage={onSendMessage}
            isStreaming={isStreaming}
            onCartFlightSelect={onCartFlightSelect}
            isCartSubmitting={isCartSubmitting}
            onClose={onClose}
            existingFlightFingerprints={existingFlightFingerprints}
            autoSearch={hasCompleteFlightSearchDefaults({
              originAirportCode,
              destinationAirportCode,
              departureDate: validDepartureDate,
              returnDate: validReturnDate,
              itinerary: validItinerary,
              fareType: fareTypeProp,
            }) && (autoSearch ?? true)}
            headerSlot={headerSlot}
            hasSavedFlights={hasSavedFlights}
            showMultiCity={showMultiCity}
            drawerPresentation={drawerPresentation}
            desktopInitialFocus={desktopInitialFocus}
            mobileInitialFocus={mobileInitialFocus}
            tripStopId={tripStopId}
            tripStops={tripStops}
          />
        </ModalProvider>
      </FlightSearchWidgetStateProvider>
    </TripDrawer>
  )
}

export default FlightSearchDrawer
